import React, { useState, useEffect, useRef, useCallback } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { Icon, IconButton, Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CellTowerIcon from "@mui/icons-material/CellTower";
import { application } from "globals/endpoints";
import loggerFactory from "globals/logger/logger-factory";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import { setBroadcastCall } from "context";
import { useMaterialUIController } from "context";
import CTLNotification from "application/components/CTLNotification";
import { setIsBroadcastCall } from "context";

export default function LazyDropdown({ compact = false }) {
  const theme = useTheme();
  const [open, setOpen] = useState(null);
  const [controller, dispatch] = useMaterialUIController();
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [visibleItems, setVisibleItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [isSelectAll, setIsSelectAll] = useState(false);
  const listRef = useRef(null);
  const itemsPerLoad = 20;
    const [notify, setNotify] = useState({
      isOpen: false,
      message: "",
      type: "",
      pagename: "",
      status: "",
    });

  useEffect(() => {
    application
      .get("extensions/listAllContacts")
      .then((response) => {
        setMenuItems(response.data.data.contacts);
        setFilteredItems(response.data.data.contacts);
        setVisibleItems(response.data.data.contacts.slice(0, itemsPerLoad));
      })
      .catch((error) => {
        loggerFactory.error("Unable to get participant list", error);
      });
  }, []);

  useEffect(() => {
    const lowercasedSearch = searchInput.toLowerCase();
    const filtered = menuItems.filter(
      (item) =>
        item.description?.toLowerCase().includes(lowercasedSearch) ||
        item.code?.toLowerCase().includes(lowercasedSearch)
    );
    setFilteredItems(filtered);
    setVisibleItems(filtered.slice(0, itemsPerLoad));
  }, [searchInput, menuItems]);

  const handleScroll = useCallback(() => {
    if (!listRef.current) return;

    const { scrollTop, clientHeight, scrollHeight } = listRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 3) {
      setVisibleItems((prev) => [
        ...prev,
        ...filteredItems.slice(prev.length, prev.length + itemsPerLoad),
      ]);

    }
  }, [filteredItems, visibleItems]);

  useEffect(() => {
    const currentListRef = listRef.current;

    if (currentListRef) {
      currentListRef.addEventListener("scroll", handleScroll);
      return () => {
        currentListRef.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  const handleGroupCall = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
    setSelectedItems([]);
    setSearchInput("");
    setIsSelectAll(false);
  };

  const handleBroadcastCall = () => {
    const broadcastNumber = AuthenticationService.getBroadcastNumber();
    if (broadcastNumber) {
      const userList = selectedItems.map((item) => item).join(",");
      if (window.disablePhone) {
        const data = {
          broadcastNumber: broadcastNumber,
          userList: userList,
        };
        console.log("Broadcast call event data send : ", data);
        window.chrome.webview.postMessage(
          JSON.stringify({
            eventType: "Broadcast Call",
            data: data,
          })
        );
      } else {
        setBroadcastCall(dispatch, {
          broadcastNumber: broadcastNumber,
          userList: userList,
        });
        setIsBroadcastCall(dispatch, true);
      }
    } else {
      setNotify({
        isOpen: true,
        pagename: "Dispatcher",
        status: "Unable to start Broadcast call",
        message: "Please Define Broadcast Number",
        type: "info",
      });
    }
    handleClose();
  };

  const handleSelect = (code) => {
    setSelectedItems((prevSelected) => {
      const isSelected = prevSelected.some((item) => item === code);
      return isSelected
        ? prevSelected.filter((item) => item !== code)
        : [...prevSelected, code];
    });
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      setSelectedItems([]);
    } else {
      const allVisible = visibleItems.map((item) => item.code);
      setSelectedItems(allVisible);
    }
    setIsSelectAll(!isSelectAll);
  };

  return (
    <>
      {compact ? (
        <Tooltip title="Broadcast Call" placement="left" arrow>
          <MDBox
            component="span"
            sx={{
              width: 62,
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.35,
            }}
          >
            <IconButton
              onClick={handleGroupCall}
              sx={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
                transition: "all 0.2s ease",
                "& .MuiSvgIcon-root": {
                  fontSize: 20,
                },
                "&:hover": {
                  bgcolor: alpha(theme.palette.success.main, 0.22),
                  transform: "translateY(-1px) scale(1.03)",
                  boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.34)}`,
                },
              }}
            >
              <CellTowerIcon />
            </IconButton>
            <MDTypography
              variant="caption"
              sx={{
                fontSize: theme.typography.pxToRem(9.5),
                lineHeight: 1.05,
                fontWeight: 600,
                textAlign: "center",
                color: theme.palette.success.main,
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Broadcast
            </MDTypography>
          </MDBox>
        </Tooltip>
      ) : (
        <MDButton
          size="medium"
          color="success"
          variant="contained"
          style={{ height: "100%" }}
          fullWidth
          sx={{ fontSize: "15px", fontWeight: 600 }}
          onClick={handleGroupCall}
          endIcon={<Icon>campaign</Icon>}
        >
          Broadcast Call
        </MDButton>
      )}
      <Menu
        id="lazy-menu"
        anchorEl={open}
        keepMounted
        open={Boolean(open)}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: "500px",
            width: "300px",
            overflow: "hidden",
          },
        }}
      >
        <MDBox p={2}>
          <TextField
            variant="outlined"
            fullWidth
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </MDBox>
        <div
          ref={listRef}
          onScroll={handleScroll}
          style={{
            maxHeight: "300px",
            overflowY: "auto",
            padding: "8px 0",
          }}
        >
          <MenuItem onClick={handleSelectAll}>
            <Checkbox checked={isSelectAll} />
            Select All
          </MenuItem>
          {visibleItems.length > 0 ? (
            visibleItems.map((item) => (
              <MenuItem key={item.code} onClick={() => handleSelect(item.code)}>
                <Checkbox checked={selectedItems.includes(item.code)} />
                <MDTypography variant="body2">
                  {item.description || item.code}
                </MDTypography>
              </MenuItem>
            ))
          ) : (
            <MenuItem>No participants found!</MenuItem>
          )}
        </div>
        <MDBox display="flex" gap="8px" p={2}>
          <MDButton color="secondary" onClick={handleClose} style={{ flex: 1 }}>
            Cancel
          </MDButton>
          <MDButton
            color="success"
            style={{ flex: 1 }}
            onClick={handleBroadcastCall}
            disabled={selectedItems.length === 0}
          >
            Broadcast
          </MDButton>
        </MDBox>
      </Menu>
      <CTLNotification notify={notify} setNotify={setNotify} />
    </>
  );
}
